import { getCanonicalDeviceStatus, INACTIVE_AFTER_MS, RUNNING_SPEED_KMH } from "./deviceStatus";
import { getTelemetrySpeed } from "./vehicleStatus";

const toTimestampMs = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (value instanceof Date) return value.getTime() || 0;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getTelemetryTimestampMs = (source = {}) =>
  toTimestampMs(
    source?.packetReceivedAt ??
      source?.lastSocketUpdate ??
      source?.packetTime ??
      source?.serverReceivedAt ??
      source?.receivedAt ??
      source?.correctedTime ??
      source?.timestamp ??
      source?.serverTime ??
      source?.lastUpdate ??
      source?.lastDataReceivedAt
  );

const flatTelemetry = (source = {}) => {
  if (!source || typeof source !== "object") return {};
  return {
    ...(source?.currentLocation || {}),
    ...(source?.liveData || {}),
    ...(source?.deviceLive || {}),
    ...(source?.liveSnapshot || {}),
    ...source,
  };
};

// All live-map surfaces must use this exact source ordering and status rule.
// A valid packet time beats an older snapshot; socket candidates are passed
// first by callers, so equal timestamps still prefer the current socket event.
export const resolveUiDeviceStatus = (device = {}, liveCandidates = []) => {
  const candidates = [
    ...(Array.isArray(liveCandidates) ? liveCandidates : [liveCandidates]),
    device?.deviceLive,
    device?.liveSnapshot,
    device?.liveData,
    device?.currentLocation,
  ]
    .filter((item) => item && typeof item === "object")
    .map(flatTelemetry);

  let selected = {};
  let selectedTimestampMs = 0;
  for (const candidate of candidates) {
    const timestampMs = getTelemetryTimestampMs(candidate);
    if (timestampMs > selectedTimestampMs) {
      selected = candidate;
      selectedTimestampMs = timestampMs;
    }
  }
  if (!selectedTimestampMs) selected = candidates[0] || {};

  const telemetry = {
    ...(flatTelemetry(device?.currentLocation) || {}),
    ...(flatTelemetry(device?.liveData) || {}),
    ...selected,
  };
  const lastPacketAt =
    getTelemetryTimestampMs(selected) ||
    getTelemetryTimestampMs(telemetry) ||
    toTimestampMs(device?.lastDataReceivedAt);
  const speed = getTelemetrySpeed(selected, device?.deviceLive, device?.liveSnapshot, device?.liveData, device?.currentLocation, telemetry);
  const status = getCanonicalDeviceStatus(
    {
      ...device,
      liveData: telemetry,
      lastDataReceivedAt: lastPacketAt || device?.lastDataReceivedAt,
    },
    telemetry
  );
  const fresh = lastPacketAt > 0 && Date.now() - lastPacketAt < INACTIVE_AFTER_MS;

  // Some trackers emit a stale ACC-OFF flag while the same fresh packet has
  // valid moving speed. Treat that contradiction uniformly in all map UIs.
  const normalizedStatus =
    fresh && speed > RUNNING_SPEED_KMH && ["running", "idle", "stopped"].includes(status)
      ? "running"
      : status;

  return {
    status: normalizedStatus,
    telemetry,
    timestampMs: lastPacketAt,
    speed,
    fresh,
  };
};
